/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, RefObject, useCallback, useEffect, ReactNode } from "react";

import { Form as AntdForm } from "antd";
import {
  FormProps as AntdFormProps,
  FormInstance,
  FormItemProps,
} from "antd/lib/form";
import { useForm } from "antd/lib/form/Form";
import cx from "classnames";
import _ from "lodash";
import _isEqual from "lodash/isEqual";
import { usePrevious } from "react-use";

import styles from "./style/Form.module.scss";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FormValues = any;

export type FormErrors = Record<string, string | string[]> | undefined;

export interface FormProps extends AntdFormProps {
  values?: FormValues;
  formRef?: RefObject<FormInstance>;
  children?: ReactNode;
}

const Form = ({
  values = undefined,
  children,
  formRef = undefined,
  form,
  ...props
}: FormProps) => {
  const [formInstance] = useForm(form);

  const bindValues = useCallback(() => {
    formInstance.resetFields();
  }, [formInstance]);

  const prevValues = usePrevious(values);

  useEffect(() => {
    if (!_isEqual(prevValues, values)) {
      bindValues();
    }
  }, [bindValues, values, prevValues]);

  const debounceValidateForm = _.debounce(() => {
    formInstance.validateFields();
  }, 50);

  const handleValuesChange = (changedValues: FormValues, val: FormValues) => {
    debounceValidateForm();

    props.onValuesChange?.(changedValues, val);
  };

  return (
    <AntdForm
      layout="vertical"
      {...props}
      form={formInstance}
      ref={formRef}
      initialValues={values}
      onValuesChange={handleValuesChange}
    >
      {children}
    </AntdForm>
  );
};

interface ItemProps extends FormItemProps {
  form?: FormInstance;
  neverShowErrorMessage?: boolean;
}

export const Item: FC<ItemProps> = ({
  form,
  className,
  neverShowErrorMessage = false,
  children,
  ...props
}) => {
  const [formInstance] = useForm(form);

  if (neverShowErrorMessage) {
    return (
      <AntdForm.Item {...props} help="" validateStatus="">
        {children}
      </AntdForm.Item>
    );
  }

  return (
    // <AntdForm.Item shouldUpdate className={cx(commonStyles.mb0)}>
    <AntdForm.Item shouldUpdate>
      {() => {
        const isTouched = formInstance?.isFieldTouched(props.name || "");
        const fieldError = formInstance?.getFieldError(props.name || "");
        const shouldShowError = isTouched && !!fieldError?.length;
        return (
          <AntdForm.Item
            help={shouldShowError ? fieldError : ""}
            validateStatus={shouldShowError ? "error" : ""}
            className={cx(className, {
              [styles.hideErrorMessage]: !shouldShowError,
            })}
            {...props}
          >
            {children}
          </AntdForm.Item>
        );
      }}
    </AntdForm.Item>
  );
};

export const { List } = AntdForm;

export default Form;

export { useForm };
